// /*!

// =========================================================
// * Vision UI Free React - v1.0.0
// =========================================================

// * Product Page: https://www.creative-tim.com/product/vision-ui-free-react
// * Copyright 2021 Creative Tim (https://www.creative-tim.com/)
// * Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

// * Design and Coded by Simmmple & Creative Tim

// =========================================================

// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

// */

/*!
=========================================================
* Vision UI Free React - v1.0.0
=========================================================
*/

import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

// MUI components
import {
  CircularProgress,
  MenuItem,
  Table as MuiTable,
  Select,
  Skeleton,
  TablePagination,
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiAvatar from "components/VuiAvatar";
import VuiTypography from "components/VuiTypography";

// Theme
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import { FaDatabase, FaFolderOpen, FaUserShield } from "react-icons/fa";

import { IconButton } from "@mui/material";
import { FirstPage, LastPage, ChevronLeft, ChevronRight } from "@mui/icons-material";

const { light, dark, primary } = colors;

function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const totalPages = Math.ceil(count / rowsPerPage);

  const btnSx = {
    padding: "3px",
    margin: "0 0px",
    color: dark.main,
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
      color: "white",
    },
    "&.Mui-disabled": {
      color: rgba(dark.main, 0.35),
    },
  };

  return (
    <VuiBox display="flex" alignItems="center" ml={1.5}>
      {/* LEFT GROUP */}
      <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0} sx={btnSx}>
        <FirstPage fontSize="small" />
      </IconButton>

      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} sx={btnSx}>
        <ChevronLeft fontSize="small" />
      </IconButton>

      {/* RIGHT GROUP */}
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= totalPages - 1}
        sx={btnSx}
      >
        <ChevronRight fontSize="small" />
      </IconButton>

      <IconButton
        onClick={(e) => onPageChange(e, totalPages - 1)}
        disabled={page >= totalPages - 1}
        sx={btnSx}
      >
        <LastPage fontSize="small" />
      </IconButton>
    </VuiBox>
  );
}

function Table({
  columns,
  rows,
  loading,
  pagination,
  paginationMode = "client",
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  emptyState = false,
  emptyTitle = "No data available",
  emptyDescription = null,
}) {
  const { grey } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  /* ---------------- Pagination State ---------------- */
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /* ---------------- Columns ---------------- */
  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl = key === 0 ? 3 : 1;
    let pr = key === columns.length - 1 ? 3 : 1;

    return (
      <VuiBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${grey[700]}`}
      >
        {name.toUpperCase()}
      </VuiBox>
    );
  });

  /* ---------------- Paginated Rows ---------------- */
  // const visibleRows = useMemo(() => {
  //   if (!pagination) return rows;
  //   return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  // }, [rows, page, rowsPerPage, pagination]);

  const visibleRows = useMemo(() => {
    if (!pagination) return rows;

    //  BACKEND PAGINATION
    if (paginationMode === "server") {
      return rows; // already paginated by API
    }

    //  FRONTEND PAGINATION
    return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [rows, page, rowsPerPage, pagination, paginationMode]);

  /* ---------------- Skeleton Rows ---------------- */
  const skeletonRows = Array.from({ length: rowsPerPage }).map((_, rowIdx) => (
    <TableRow key={`skeleton-${rowIdx}`}>
      {columns.map((_, colIdx) => (
        <VuiBox
          key={colIdx}
          component="td"
          p={1.5}
          borderBottom={`${borderWidth[1]} solid ${grey[700]}`}
        >
          <Skeleton variant="text" height={18} width="80%" sx={{ bgcolor: "grey.600" }} />
        </VuiBox>
      ))}
    </TableRow>
  ));

  /* ---------------- Render Rows ---------------- */
  const renderRows = visibleRows.map((row, key) => (
    <TableRow key={`row-${key}`}>
      {columns.map(({ name, align }) => {
        const value = row[name];

        if (Array.isArray(value)) {
          return (
            <VuiBox
              key={uuidv4()}
              component="td"
              p={1}
              borderBottom={`${borderWidth[1]} solid ${grey[700]}`}
            >
              <VuiBox display="flex" alignItems="center">
                <VuiAvatar
                  src={value[0]}
                  name={value[1]}
                  variant="rounded"
                  size="sm"
                  sx={{ mr: 2 }}
                />
                <VuiTypography variant="button" fontWeight="medium">
                  {value[1]}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          );
        }

        return (
          <VuiBox
            key={uuidv4()}
            component="td"
            p={1}
            textAlign={align}
            borderBottom={`${borderWidth[1]} solid ${grey[700]}`}
          >
            <VuiTypography
              variant="button"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                maxWidth: "100%",
              }}
            >
              {value}
            </VuiTypography>
          </VuiBox>
        );
      })}
    </TableRow>
  ));

  /* ---------------- Render ---------------- */
  return (
    <TableContainer
      sx={{ minHeight: 300, position: "relative", display: "flex", flexDirection: "column" }}
    >
      {/* <MuiTable>
        <VuiBox component="thead">
          <TableRow>{renderColumns}</TableRow>
        </VuiBox>

   
        <TableBody>{renderRows}</TableBody>
      </MuiTable> */}

      <VuiBox
        sx={{
          overflowY: "auto",
        }}
      >
        <MuiTable>
          <VuiBox component="thead">
            <TableRow>{renderColumns}</TableRow>
          </VuiBox>

          {/* <TableBody>{renderRows}</TableBody> */}
          <TableBody>
            {renderRows.length > 0 ? (
              renderRows
            ) : emptyState && !loading ? (
              <TableRow>
                <VuiBox
                  component="td"
                  colSpan={columns.length}
                  textAlign="center"
                  py={6}
                  sx={{ opacity: 0.75 }}
                >
                  <VuiBox display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                    <FaFolderOpen size={26} color="rgba(255,255,255,0.35)" />

                    <VuiTypography variant="button" fontWeight="regular">
                      {emptyTitle}
                    </VuiTypography>

                    {emptyDescription && (
                      <VuiTypography variant="caption" sx={{ opacity: 0.6 }}>
                        {emptyDescription}
                      </VuiTypography>
                    )}
                  </VuiBox>
                </VuiBox>
              </TableRow>
            ) : null}
          </TableBody>
        </MuiTable>
      </VuiBox>

      {pagination && (
        <VuiBox
          mt="auto"
          pt={2}
          pb={1}
          pr={1}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.25}
        >
          <VuiBox
            display="flex"
            alignItems="center"
            gap={0.75}
            sx={{
              height: "34px",
              px: 1.25,
              borderRadius: "10px",
              background: "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03))",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <VuiTypography variant="caption" sx={{ opacity: 0.7 }}>
              Rows
            </VuiTypography>
            {/* <Select
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(e.target.value)}
              size="small"
              sx={{
                color: "white",
                fontSize: "0.75rem",
                "& .MuiSelect-icon": { color: "white" },

                "& fieldset": {
                  border: "none",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: "linear-gradient(135deg, rgba(30,30,30,0.95), rgba(20,20,20,0.9))",
                    backdropFilter: "blur(12px)",
                    borderRadius: "10px",
                  },
                },
              }}
            >
              {[5, 10, 25].map((n) => (
                <MenuItem
                  key={n}
                  value={n}
                  sx={{
                    fontSize: "0.75rem",
                    color: "white",

                    "&:hover": {
                      background: "rgba(255,255,255,0.08)",
                    },

                    "&.Mui-selected": {
                      background: "rgba(255,255,255,0.12)",
                    },

                    "&.Mui-selected:hover": {
                      background: "rgba(255,255,255,0.16)",
                    },
                  }}
                >
                  {n}
                </MenuItem>
              ))}
            </Select> */}
            <select
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              style={{
                background: "transparent",
                color: dark.main,
                border: "none",
                outline: "none",
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              {[5, 10, 25].map((n) => (
                <option key={n} value={n} style={{ color: "black" }}>
                  {n}
                </option>
              ))}
            </select>
          </VuiBox>

          <TablePagination
            component="div"
            disabled={loading}
            count={total} // backend total
            page={page} // controlled
            ActionsComponent={TablePaginationActions}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            rowsPerPageOptions={[]}
            sx={{
              minHeight: "34px",

              "& .MuiTablePagination-toolbar": {
                minHeight: "34px",
                paddingLeft: 0,
                paddingRight: 0,
              },

              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
                color: dark.main,
                marginRight: "0px",
              },

              "& .MuiTablePagination-actions": {
                marginLeft: "5px",
              },

              "& .MuiIconButton-root": {
                padding: "4px",
                color: "white",
              },
            }}
          />
        </VuiBox>
      )}

      {/* {pagination && (
        <TablePagination
          component="div"
          disabled={loading}
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )} */}

      {loading && (
        <VuiBox
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.07)",
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ mb: 1 }} />
          {/* <VuiTypography variant="caption">
            Loading data…
          </VuiTypography> */}
        </VuiBox>
      )}
    </TableContainer>
  );
}

/* ---------------- Defaults ---------------- */
Table.defaultProps = {
  columns: [],
  rows: [],
  loading: false,
  pagination: false,
  rowsPerPageOptions: [5, 10, 25],
  defaultRowsPerPage: 5,
};

/* ---------------- PropTypes ---------------- */
Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  pagination: PropTypes.bool,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  defaultRowsPerPage: PropTypes.number,
};

export default Table;
